import { useState } from 'react';

interface VideoCard {
  id: number;
  title: string;
  description: string;
  youtubeId: string;
}

function VideoEmbed({ video }: { video: VideoCard }) {
  const [playing, setPlaying] = useState(false);

  const handlePlay = () => {
    setPlaying(true);
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>

      {playing ? (
        <iframe
          className="w-full h-full"
          src={`https://www.youtube.com/embed/${video.youtubeId}?autoplay=1&rel=0&vq=hd1080`}
          title={video.title}
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        ></iframe>
      ) : (
        <>
          {/* HD thumbnail — only loaded before play, no iframe graininess */}
          <img
            src={`https://i.ytimg.com/vi/${video.youtubeId}/maxresdefault.jpg`}
            alt={video.title}
            className="w-full h-full"
            style={{ objectFit: 'cover', objectPosition: 'top' }}
          />

          {/* Green play button overlay */}
          <div
            onClick={handlePlay}
            className="absolute inset-0 flex items-center justify-center cursor-pointer"
            style={{ zIndex: 20 }}
          >
            <div className="absolute inset-0 bg-black/10"></div>
            <div
              className="relative flex items-center justify-center rounded-full transition-transform duration-200 hover:scale-110 active:scale-95"
              style={{
                width: '93px',
                height: '93px',
                backgroundColor: '#16a34a',
                boxShadow: '0 4px 24px rgba(22,163,74,0.45)',
              }}
            >
              <div
                style={{
                  width: 0,
                  height: 0,
                  borderTop: '14px solid transparent',
                  borderBottom: '14px solid transparent',
                  borderLeft: '22px solid white',
                  marginLeft: '5px',
                }}
              ></div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function Videos() {
  const videoPlaceholders: VideoCard[] = [
    {
      id: 1,
      title: "Sarah's Success Story",
      description: "How we bought a run-down property quickly for all cash",
      youtubeId: "oZyU3yzN9SA"
    },
    {
      id: 2,
      title: "Tired Landlord in Texas",
      description: "Williams sold his troubled rentals to move forward with his life and retire",
      youtubeId: "ZrPjxZTs3Sw"
    },
    {
      id: 3,
      title: "Inherited Property",
      description: "How we turned Jason's inherited property into quick cash and saved him from all the headaches",
      youtubeId: "F2hhp6ds68A"
    }
  ];

  return (
    <section id="videos" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Success <span className="text-emerald-600">Stories</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Hear from real homeowners who have successfully sold their properties with GreenState Investments. Their stories showcase our commitment to fast, fair, and hassle-free transactions.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {videoPlaceholders.map((video) => (
            <div key={video.id} className="bg-gray-50 rounded-lg overflow-hidden hover:shadow-xl transition-shadow" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
              {/* Video Area */}
              <div className="relative bg-gray-800 flex items-center justify-center" style={{ aspectRatio: '9/16' }}>
                <VideoEmbed video={video} />
              </div>

              {/* Video Info */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{video.title}</h3>
                <p className="text-gray-600 text-sm">{video.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Testimonials */}
        <div className="mt-20 grid md:grid-cols-2 gap-8">
          <div className="bg-gray-50 rounded-lg p-8" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
            <div className="flex items-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <i key={i} className="ri-star-fill text-xl text-yellow-400"></i>
              ))}
            </div>
            <p className="text-gray-700 mb-6 leading-relaxed italic">
              "GreenState Investments made selling my house incredibly easy. I was facing foreclosure and they gave me a fair cash offer within 24 hours. The whole process took less than two weeks. I can't thank them enough!"
            </p>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-emerald-600 text-white rounded-full font-bold">
                JM
              </div>
              <div>
                <p className="font-semibold text-gray-900">John Martinez</p>
                <p className="text-sm text-gray-600">Houston, TX</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-8" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
            <div className="flex items-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <i key={i} className="ri-star-fill text-xl text-yellow-400"></i>
              ))}
            </div>
            <p className="text-gray-700 mb-6 leading-relaxed italic">
              "After inheriting my parents' home, I didn't know what to do. GreenState Investments walked me through everything and bought the house as-is. No repairs, no stress. Highly recommend their services!"
            </p>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center bg-emerald-600 text-white rounded-full font-bold">
                SC
              </div>
              <div>
                <p className="font-semibold text-gray-900">Donna Chen</p>
                <p className="text-sm text-gray-600">Portland, OR</p>
              </div>
            </div>
          </div>
        </div>

        {/* Credentials Bar */}
        <div className="mt-16 bg-gray-50 rounded-2xl border border-gray-100 px-10 py-10 flex flex-col md:flex-row items-center justify-center gap-10 md:gap-16" style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>

          {/* Stat: Homes Purchased */}
          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 rounded-full mb-3">
              <i className="ri-home-4-line text-2xl text-emerald-600"></i>
            </div>
            <p className="text-sm font-semibold text-gray-500 uppercase tracking-widest mb-1">Homes Purchased</p>
            <p className="text-5xl font-extrabold text-emerald-600 leading-none">2,100+</p>
          </div>

          {/* Divider */}
          <div className="hidden md:block w-px h-24 bg-gray-200"></div>

          {/* Stat: Years in Business */}
          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 rounded-full mb-3">
              <i className="ri-calendar-2-line text-2xl text-emerald-600"></i>
            </div>
            <p className="text-sm font-semibold text-gray-500 uppercase tracking-widest mb-1">Years in Business</p>
            <p className="text-5xl font-extrabold text-emerald-600 leading-none">28+</p>
          </div>

          {/* Divider */}
          <div className="hidden md:block w-px h-24 bg-gray-200"></div>

          {/* BBB Logo */}
          <div className="flex flex-col items-center text-center">
            <img
              src="https://public.readdy.ai/ai/img_res/edited_1855927c698b6dd27cbfad30c0351e5c_00f03e19.jpg"
              alt="BBB Accredited Business A+"
              className="h-16 w-auto object-contain"
            />
            <p className="text-sm font-semibold text-gray-500 uppercase tracking-widest mt-3">Accredited Business</p>
          </div>

        </div>
      </div>
    </section>
  );
}
